/**
 * Sora角色创建节点 - 用于创建、训练和查询Sora角色
 * 
 * 功能说明：
 * 1. 创建角色（输入图片和视频）
 * 2. 提交角色训练
 * 3. 查询角色详情和状态
 * 
 * 连接说明：
 * - 输入1：参考图片（imageReference, imageTask, imageDisplay等）
 * - 输入2：参考视频（videoTask等）
 */
import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import { useReactFlow, useStore, Handle, Position } from '@xyflow/react';
import { useHistory } from 'react-router-dom';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ApiService from '../../../api/ApiService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import './SoraRoleCreateNode.css';

const apiService = new ApiService();

export const SoraRoleCreateNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const { duplicateNode } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const { readOnly } = useProject();
  const history = useHistory();
  
  // 表单字段状态
  const [name, setName] = useState(data.name || '');
  const [description, setDescription] = useState(data.description || '');
  const [categoryId, setCategoryId] = useState(data.categoryId || '');
  const [isPublic, setIsPublic] = useState(data.isPublic !== undefined ? data.isPublic : 1);
  
  // 角色状态
  const [roleId, setRoleId] = useState(data.roleId || null);
  const [roleStatus, setRoleStatus] = useState(data.roleStatus || null);
  const [taskId, setTaskId] = useState(data.taskId || null);
  
  // UI状态
  const [categories, setCategories] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isTraining, setIsTraining] = useState(false);
  const [isQuerying, setIsQuerying] = useState(false);
  const [error, setError] = useState(null);
  
  // 轮询相关
  const mountedRef = useRef(true);
  const pollingIntervalRef = useRef(null);
  const hasCheckedInitialStatus = useRef(false); // 用于追踪是否已检查过初始状态

  // 同步节点数据
  useEffect(() => {
    updateNodeData(id, {
      name,
      description,
      categoryId,
      isPublic,
      roleId,
      roleStatus,
      taskId,
    });
  }, [id, name, description, categoryId, isPublic, roleId, roleStatus, taskId, updateNodeData]);

  // 组件卸载清理
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // 获取分类列表
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiService.request(
          `${apiService.apiBase}/character/categories`,
          'GET'
        );
        
        if (response.code === 200 && response.data?.list) {
          setCategories(response.data.list);
          // 如果还没有选择分类，默认选择第一个
          if (!categoryId && response.data.list.length > 0) {
            setCategoryId(response.data.list[0].id);
          }
        }
      } catch (err) {
        console.error('获取分类列表失败:', err);
      }
    };
    
    fetchCategories();
  }, []);

  // 恢复轮询 - 页面刷新后如果角色正在训练中，自动恢复轮询
  useEffect(() => {
    // 只在首次检测到训练中状态时启动轮询，避免重复启动
    if (!hasCheckedInitialStatus.current && roleId && roleStatus && (roleStatus === 2 || roleStatus === 3)) {
      console.log('SoraRoleCreateNode - 检测到训练中状态，恢复轮询:', { roleId, roleStatus });
      hasCheckedInitialStatus.current = true;
      startPolling(roleId);
    }
  }, [roleId, roleStatus]); // 监听 roleId 和 roleStatus 变化

  // 获取连接的图片和视频
  const getConnectedMedia = useCallback(() => {
    const imageEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'image'
    );
    const videoEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'video'
    );
    
    let referenceImage = null;
    let referenceVideo = null;
    
    // 获取图片
    if (imageEdges.length > 0) {
      const imageNode = getNode(imageEdges[0].source);
      if (imageNode) {
        console.log('SoraRoleCreateNode - 图片节点类型:', imageNode.type);
        console.log('SoraRoleCreateNode - 图片节点数据:', imageNode.data);
        
        // 根据节点类型获取不同的字段
        if (imageNode.type === 'imageTask') {
          // ImageTaskNode 使用 generatedImages 数组
          const images = imageNode.data?.generatedImages;
          if (images && images.length > 0) {
            const imageData = images[0];
            // 图片可能是对象或字符串，如果是对象则提取 url 字段
            referenceImage = typeof imageData === 'object' ? imageData.url : imageData;
          }
        } else if (imageNode.type === 'imageReference') {
          // ImageReferenceNode 使用 imageUrl
          referenceImage = imageNode.data?.imageUrl;
        } else if (imageNode.type === 'imageDisplay') {
          // ImageDisplayNode 使用 imageUrl
          referenceImage = imageNode.data?.imageUrl;
        } else if (imageNode.type === 'drawing') {
          // DrawingNode 使用 imageData
          referenceImage = imageNode.data?.imageData;
        } else {
          // 其他节点尝试常见字段
          referenceImage = imageNode.data?.imageUrl || imageNode.data?.url;
        }
        
        console.log('SoraRoleCreateNode - 提取的图片URL:', referenceImage);
      }
    }
    
    // 获取视频
    if (videoEdges.length > 0) {
      const videoNode = getNode(videoEdges[0].source);
      if (videoNode) {
        console.log('SoraRoleCreateNode - 视频节点类型:', videoNode.type);
        console.log('SoraRoleCreateNode - 视频节点数据:', videoNode.data);
        
        let videoUrl = null;
        
        // VideoTaskNode 使用 videoUrl
        if (videoNode.type === 'videoTask') {
          videoUrl = videoNode.data?.videoUrl;
        } else if (videoNode.type === 'videoReference') {
          // VideoReferenceNode 使用 videoUrl
          videoUrl = videoNode.data?.videoUrl;
          
          // 检查是否还在上传中
          if (videoNode.data?.isUploading) {
            console.warn('SoraRoleCreateNode - 视频还在上传中');
            videoUrl = null;
          }
        } else {
          // 其他节点尝试常见字段
          videoUrl = videoNode.data?.videoUrl || videoNode.data?.url;
        }
        
        // 过滤掉本地预览URL（data: 或 blob:）
        if (videoUrl && (videoUrl.startsWith('data:') || videoUrl.startsWith('blob:'))) {
          console.warn('SoraRoleCreateNode - 视频URL是本地预览，需要等待上传完成');
          videoUrl = null;
        }
        
        referenceVideo = videoUrl;
        console.log('SoraRoleCreateNode - 提取的视频URL:', referenceVideo);
      }
    } else {
      console.log('SoraRoleCreateNode - 没有找到视频连接边');
    }
    
    return { referenceImage, referenceVideo };
  }, [edges, getNode, id]);

  // 验证连接
  const isValidConnection = useCallback((connection) => {
    if (connection.targetHandle === 'image') {
      const sourceNode = getNode(connection.source);
      return ['imageReference', 'drawing', 'imageTask', 'imageDisplay'].includes(sourceNode?.type);
    } else if (connection.targetHandle === 'video') {
      const sourceNode = getNode(connection.source);
      return ['videoTask', 'videoReference'].includes(sourceNode?.type);
    }
    return true;
  }, [getNode]);

  // 创建角色
  const handleCreateRole = async () => {
    try {
      setError(null);
      setIsCreating(true);
      
      // 验证必填字段
      if (!name.trim()) {
        throw new Error('请输入角色名称');
      }
      
      if (!categoryId) {
        throw new Error('请选择角色分类');
      }
      
      // 获取用户 token（提前获取用于检查角色名）
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        throw new Error('请先登录');
      }
      
      // 检查角色名是否可用
      const checkResult = await apiService.check_role_name(userData.token, name.trim());
      if (!checkResult.data?.available) {
        throw new Error(checkResult.data?.message || '该角色名不可用');
      }
      
      // 获取连接的图片和视频
      const { referenceImage, referenceVideo } = getConnectedMedia();
      
      // 添加调试日志
      console.log('SoraRoleCreateNode - 连接的媒体:', {
        referenceImage,
        referenceVideo,
        edges: edges.filter(e => e.target === id),
      });
      
      if (!referenceImage) {
        throw new Error('请连接参考图片节点');
      }
      
      if (!referenceVideo) {
        // 检查是否连接了视频节点但还没上传完成
        const videoEdges = edges.filter(e => e.target === id && e.targetHandle === 'video');
        if (videoEdges.length > 0) {
          throw new Error('请等待视频上传完成后再创建角色');
        }
        throw new Error('请连接参考视频节点');
      }
      
      console.log('SoraRoleCreateNode - 创建角色请求参数:', {
        name: name.trim(),
        reference_image: referenceImage,
        reference_video: referenceVideo,
        category_id: categoryId,
        is_public: isPublic,
      });
      
      // 调用创建角色 API（使用与 Step5_Training 动漫风格相同的接口）
      const response = await apiService.create_role(
        userData.token,
        name.trim(),
        '', // pen_name 字段已移除
        referenceImage,
        referenceVideo,
        categoryId,
        isPublic
      );
      
      if (response.code === 200 && response.data) {
        const newRoleId = response.data.id;
        const newRoleStatus = response.data.status;
        setRoleId(newRoleId);
        setRoleStatus(newRoleStatus);
        toast.success(`角色创建成功！角色ID: ${newRoleId}`);
        
        // 自动提交训练
        if (newRoleStatus === 0) {
          toast.info('正在自动提交训练...');
          await submitTraining(newRoleId);
        }
      } else {
        throw new Error(response.msg || response.message || '创建角色失败');
      }
      
    } catch (err) {
      console.error('创建角色失败:', err);
      const errorMessage = err?.data?.detail || err?.message || '创建角色失败';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  // 提交训练 - 内部函数
  const submitTraining = async (targetRoleId) => {
    try {
      setError(null);
      setIsTraining(true);
      
      const useRoleId = targetRoleId || roleId;
      if (!useRoleId) {
        throw new Error('请先创建角色');
      }
      
      // 获取用户 token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        throw new Error('请先登录');
      }
      
      // 调用提交训练 API
      const response = await apiService.request(
        `${apiService.apiBase}/character/role/submit_training`,
        'POST',
        {
          token: userData.token,
          role_id: useRoleId,
        }
      );
      
      if (response.code === 200 && response.data) {
        setTaskId(response.data.task_id);
        setRoleStatus(response.data.status);
        // toast.success('训练任务提交成功！');
        
        // 开始轮询任务状态，传入 roleId 以避免状态更新延迟问题
        startPolling(useRoleId);
      } else {
        throw new Error(response.msg || response.message || '提交训练失败');
      }
      
    } catch (err) {
      console.error('提交训练失败:', err);
      const errorMessage = err?.data?.detail || err?.message || '提交训练失败';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsTraining(false);
    }
  };

  // 提交训练 - 按钮点击处理
  const handleSubmitTraining = async () => {
    await submitTraining(roleId);
  };

  // 查询角色详情
  const handleQueryRole = async (targetRoleId) => {
    try {
      setError(null);
      setIsQuerying(true);
      
      const useRoleId = targetRoleId || roleId;
      if (!useRoleId) {
        throw new Error('请先创建角色');
      }
      
      // 获取用户 token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        throw new Error('请先登录');
      }
      
      // 调用查询详情 API
      const response = await apiService.request(
        `${apiService.apiBase}/character/role/detail`,
        'POST',
        {
          token: userData.token,
          role_id: useRoleId,
        }
      );
      
      if (response.code === 200 && response.data) {
        setRoleStatus(response.data.status);
        // toast.success('角色详情查询成功！');
        
        // 如果训练完成，停止轮询
        if (response.data.status === 1) {
          // toast.success('角色训练已完成！');
          stopPolling();
        } else if (response.data.status === 4) {
          toast.error('角色训练失败');
          stopPolling();
        }
      } else {
        throw new Error(response.msg || response.message || '查询角色详情失败');
      }
      
    } catch (err) {
      console.error('查询角色详情失败:', err);
      const errorMessage = err?.data?.detail || err?.message || '查询角色详情失败';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsQuerying(false);
    }
  };

  // 查看详情（跳转到详情页）
  const handleViewDetail = () => {
    if (!roleId) {
      toast.error('角色ID不存在');
      return;
    }
    
    // 跳转到角色详情页面
    history.push(`/role/${roleId}`);
  };

  // 复制角色名字
  const handleCopyRoleName = async () => {
    try {
      const textToCopy = `@${name} `;
      await navigator.clipboard.writeText(textToCopy);
      toast.success('角色名字已复制到剪贴板');
    } catch (err) {
      console.error('复制失败:', err);
      toast.error('复制失败，请重试');
    }
  };

  // 开始轮询
  const startPolling = (targetRoleId) => {
    if (pollingIntervalRef.current) return;
    
    const useRoleId = targetRoleId || roleId;
    pollingIntervalRef.current = setInterval(() => {
      handleQueryRole(useRoleId);
    }, 10000); // 每10秒轮询一次
  };

  // 停止轮询
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // 复制节点
  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateNode(id);
  };

  // 获取状态显示文本
  const getStatusText = () => {
    if (roleStatus === null) return '未创建';
    const statusMap = {
      0: '草稿（未训练）',
      1: '训练完成',
      2: '训练中',
      3: '等待训练',
      4: '训练失败',
    };
    return statusMap[roleStatus] || `未知状态(${roleStatus})`;
  };

  // 获取状态徽章类名
  const getStatusBadgeClass = () => {
    if (roleStatus === null) return 'SoraRoleCreateNode-status-draft';
    const statusClassMap = {
      0: 'SoraRoleCreateNode-status-draft',
      1: 'SoraRoleCreateNode-status-completed',
      2: 'SoraRoleCreateNode-status-processing',
      3: 'SoraRoleCreateNode-status-pending',
      4: 'SoraRoleCreateNode-status-failed',
    };
    return statusClassMap[roleStatus] || 'SoraRoleCreateNode-status-draft';
  };

  return (
    <div className="SoraRoleCreateNode-container">
      {/* 复制按钮 */}
      <button 
        className="SoraRoleCreateNode-duplicate-button"
        onClick={handleDuplicate}
        title="复制节点"
      >
        <i className="bi bi-copy"></i>
      </button>

      {/* 图片输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="image"
        className="SoraRoleCreateNode-handle SoraRoleCreateNode-handle-image"
        style={{ top: '30%' }}
        isValidConnection={isValidConnection}
      >
        <div className="SoraRoleCreateNode-handle-label">参考图</div>
      </Handle>

      {/* 视频输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="video"
        className="SoraRoleCreateNode-handle SoraRoleCreateNode-handle-video"
        style={{ top: '70%' }}
        isValidConnection={isValidConnection}
      >
        <div className="SoraRoleCreateNode-handle-label">参考视频</div>
      </Handle>

      <div className="SoraRoleCreateNode-title">SORA 角色创建</div>

      <div className="SoraRoleCreateNode-wrapper">
        {/* 角色ID和状态 */}
        {roleId && (
          <div className="SoraRoleCreateNode-info">
            <div className="SoraRoleCreateNode-info-row">
              <span className="SoraRoleCreateNode-info-label">角色ID:</span>
              <span className="SoraRoleCreateNode-info-value">{roleId}</span>
            </div>
            <div className="SoraRoleCreateNode-info-row">
              <span className="SoraRoleCreateNode-info-label">状态:</span>
              <span className={`SoraRoleCreateNode-status-badge ${getStatusBadgeClass()}`}>
                {getStatusText()}
              </span>
            </div>
            {taskId && (
              <div className="SoraRoleCreateNode-info-row">
                <span className="SoraRoleCreateNode-info-label">任务ID:</span>
                <span className="SoraRoleCreateNode-info-value">{taskId}</span>
              </div>
            )}
          </div>
        )}

        {/* 角色名称 */}
        <div className="SoraRoleCreateNode-field">
          <label className="SoraRoleCreateNode-label">角色名称 *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="请输入角色名称"
            disabled={readOnly || !!roleId}
            className="SoraRoleCreateNode-input"
            maxLength={100}
          />
        </div>

        {/* 角色描述 */}
        <div className="SoraRoleCreateNode-field">
          <label className="SoraRoleCreateNode-label">角色描述</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="请输入角色描述"
            disabled={readOnly || !!roleId}
            className="SoraRoleCreateNode-textarea"
            rows={3}
          />
        </div>

        {/* 分类选择 */}
        <div className="SoraRoleCreateNode-field">
          <label className="SoraRoleCreateNode-label">角色分类 *</label>
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(Number(e.target.value))}
            disabled={readOnly || !!roleId || categories.length === 0}
            className="SoraRoleCreateNode-select"
          >
            <option value="">请选择分类</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        {/* 是否公开 */}
        <div className="SoraRoleCreateNode-field">
          <label className="SoraRoleCreateNode-label">是否公开 *</label>
          <div className="SoraRoleCreateNode-button-group">
            <button
              className={`SoraRoleCreateNode-button ${isPublic === 1 ? 'SoraRoleCreateNode-button-active' : ''}`}
              onClick={() => setIsPublic(1)}
              disabled={readOnly || !!roleId}
            >
              公开
            </button>
            <button
              className={`SoraRoleCreateNode-button ${isPublic === 0 ? 'SoraRoleCreateNode-button-active' : ''}`}
              onClick={() => setIsPublic(0)}
              disabled={readOnly || !!roleId}
            >
              私有
            </button>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="SoraRoleCreateNode-actions">
          {/* 创建角色按钮 */}
          {!roleId && (
            <button
              onClick={handleCreateRole}
              disabled={isCreating || readOnly}
              className={`SoraRoleCreateNode-action-button SoraRoleCreateNode-create-button ${
                isCreating || readOnly ? 'SoraRoleCreateNode-button-disabled' : ''
              }`}
            >
              {isCreating ? '创建中...' : '创建角色'}
            </button>
          )}

          {/* 提交训练按钮 */}
          {roleId && roleStatus === 0 && (
            <button
              onClick={handleSubmitTraining}
              disabled={isTraining || readOnly}
              className={`SoraRoleCreateNode-action-button SoraRoleCreateNode-train-button ${
                isTraining || readOnly ? 'SoraRoleCreateNode-button-disabled' : ''
              }`}
            >
              {isTraining ? '提交中...' : '提交训练'}
            </button>
          )}

          {/* 查询详情按钮 */}
          {roleId && (
            <>
              <button
                onClick={roleStatus === 1 ? handleViewDetail : () => handleQueryRole()}
                disabled={isQuerying}
                className="SoraRoleCreateNode-action-button SoraRoleCreateNode-query-button"
              >
                {isQuerying ? '查询中...' : (roleStatus === 1 ? '查看详情' : '刷新状态')}
              </button>
              
              {/* 复制角色名字按钮 - 只在训练完成时显示 */}
              {roleStatus === 1 && (
                <button
                  onClick={handleCopyRoleName}
                  className="SoraRoleCreateNode-action-button SoraRoleCreateNode-copy-button"
                  title="复制角色名字"
                >
                  <i className="bi bi-clipboard"></i> 复制名字
                </button>
              )}
            </>
          )}
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="SoraRoleCreateNode-error">
            ❌ {error}
          </div>
        )}

        {/* 使用说明 */}
        <div className="SoraRoleCreateNode-hint">
          💡 连接参考图和参考视频，填写信息后创建角色，然后提交训练
        </div>
      </div>
    </div>
  );
});

SoraRoleCreateNode.displayName = 'SoraRoleCreateNode';

